import { gql } from '@apollo/client'
import { InvoiceFragment } from './invoice'

const PricingFragment = gql`
  fragment PricingFragment on PricingNode {
    net
    tax
    taxRate
    gross
    currency
  }
`

const PlanFragment = gql`
  fragment PlanFragment on PlanNode {
    id
    name
    description
    code
    active
    autoRenew
    promotion {
      id
      code
      name
      promotionType
      description
      discount
      startAt
      endAt
      duration
    }
    isFreeTrialAvailable
  }
`

const PlanCostFragment = gql`
  fragment PlanCostFragment on PlanCostNode {
    id
    billingCycle
    pricing {
      ...PricingFragment
    }
    taxRate
    plan {
      ...PlanFragment
      features {
        code
        enabled
        description
      }
    }
  }
  ${PricingFragment}
  ${PlanFragment}
`

const SubscriptionFragment = gql`
  fragment SubscriptionFragment on SubscriptionNode {
    id
    startAt
    endAt
    billingClosed
    active
    planCost {
      ...PlanCostFragment
    }
    promotion {
      name
      description
      discount
      endAt
      active
      promotionType
    }
    pricing {
      ...PricingFragment
    }
  }
  ${PricingFragment}
  ${PlanCostFragment}
`

const PaymentMethodFragment = gql`
  fragment PaymentMethodFragment on PaymentMethodNode {
    brand
    last4
    expMonth
    expYear
    canRemove
    isExpired
  }
`

export const SUBSCRIPTION_LIST_QUERY = gql`
  query Subscriptions {
    subscriptions {
      edges {
        node {
          ...SubscriptionFragment
        }
      }
    }
  }
  ${SubscriptionFragment}
`

export const SUBSCRIPTION_CHARGE_LIST_QUERY = gql`
  query Charges {
    charges {
      edges {
        node {
          id
          lastChargeAttempt
          billingPeriods {
            price
            taxRate
            currency
            startAt
            endAt
            subscription {
              ...SubscriptionFragment
            }
          }
          invoice {
            ...InvoiceFragment
          }
          pricing {
            ...PricingFragment
          }
        }
      }
    }
  }
  ${InvoiceFragment}
  ${SubscriptionFragment}
  ${PricingFragment}
`

export const ACTIVE_PLANS_QUERY = gql`
  query ActivePlans {
    activePlans {
      edges {
        node {
          ...PlanFragment
          features {
            code
            enabled
            description
          }
          costs {
            id
            billingCycle
            currency
            pricing {
              ...PricingFragment
            }
          }
        }
      }
    }
  }
  ${PlanFragment}
  ${PricingFragment}
`

export const PLAN_COST_QUERY = gql`
  query PlanCost($planCode: String!, $billingCycle: String!) {
    planCost(planCode: $planCode, billingCycle: $billingCycle) {
      ...PlanCostFragment
    }
  }
  ${PlanCostFragment}
`

export const SUBSCRIPTION_STATE_QUERY = gql`
  query SubscriptionState {
    subscriptionState {
      active {
        ...SubscriptionFragment
      }
      future {
        ...SubscriptionFragment
      }
      billingAccount {
        firstName
        lastName
        email
        isSuspended
        suspendedAt
        unsuspendedAt
        suspendedReason
        dueAmount {
          ...PricingFragment
        }
        isFreeTrialEligible
      }
    }
  }
  ${SubscriptionFragment}
  ${PricingFragment}
`

export const PAYMENT_METHOD_QUERY = gql`
  query PaymentMethod {
    paymentMethod {
      ...PaymentMethodFragment
    }
  }
  ${PaymentMethodFragment}
`

export const ACTIVATE_SUBSCRIPTION = gql`
  mutation ActivateSubscription($plan: String!, $billingCycle: String!){
    activateSubscription(plan: $plan, billingCycle: $billingCycle) {
      subscription {
        ...SubscriptionFragment
      }
      errors {
        field
        messages
      }
      result
    }
  }
  ${SubscriptionFragment}
`

export const SETUP_PAYMENT_INTENT = gql`
  mutation SetupPaymentIntent {
    setupPaymentIntent {
      clientSecret
      publicKey
    }
  }
`

export const VERIFY_STRIPE_CALLBACK = gql`
  mutation VerifyStripeCallback(
    $planCode: String
    $billingCycle: String
    $setupIntent: String
    $setupIntentClientSecret: String
    $redirectStatus: String
  ) {
    verifyStripeCallback(
      planCode: $planCode
      billingCycle: $billingCycle
      setupIntent: $setupIntent
      setupIntentClientSecret: $setupIntentClientSecret
      redirectStatus: $redirectStatus
    ) {
      result
      errors {
        field
        messages
      }
    }
  }
`

export const REMOVE_CREDIT_CARD = gql`
  mutation RemoveCreditCard {
    removeCreditCard {
      paymentMethod {
        ...PaymentMethodFragment
      }
    }
  }
  ${PaymentMethodFragment}
`
